/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Tag } from "primereact/tag";
import { Skeleton } from "primereact/skeleton";
import {
  Boton,
  Titulo,
  Formulario,
  Sidebar,
  accessAPI,
  formatFecha,
  tituloBase,
  TablaFiltrable
} from "../../../shared";

/**
 * Contadores del organismo.
 *
 * Los usa la numeración configurable de procesos y certificados: el script pide
 * una clave y, si no existía, nace acá. Por eso la pantalla muestra cuáles se
 * crearon solos y cuándo se usaron por última vez — un contador que nació solo y
 * nunca se volvió a usar suele ser un typo en un script.
 *
 * El valor se edita porque hace falta para el onboarding: un organismo que llega
 * a mitad de año tiene que arrancar donde estaba, no en cero.
 */
export default function Contadores() {
  const [contadores, setContadores] = useState(null);
  const [editando, setEditando] = useState(null);
  const [loader, setLoader] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const toast = useRef(null);

  useEffect(() => {
    cargar();
    document.title = "Contadores";
    return () => (document.title = tituloBase());
  }, []);

  function cargar() {
    accessAPI(
      "GET",
      "admin/config/contador",
      null,
      (respuesta) => setContadores(respuesta),
      (respuesta) => {
        setContadores([]);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta?.[0]?.msg || "No se pudieron cargar los contadores",
          sticky: true
        });
      }
    );
  }

  function guardar(campos) {
    setLoader(true);
    accessAPI(
      "PUT",
      `admin/config/contador/${encodeURIComponent(editando.nombre)}`,
      { valor: campos.valor },
      (respuesta) => {
        setContadores(respuesta);
        setEditando(null);
        setLoader(false);
        toast.current?.show({
          severity: "success",
          summary: "Contador actualizado",
          life: 3000
        });
      },
      (respuesta) => {
        setLoader(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta?.[0]?.msg || "No se pudo actualizar el contador",
          sticky: true
        });
      }
    );
  }

  const configFormulario = editando
    ? {
        secciones: [
          {
            campos: [
              {
                nombre: "valor",
                tipoitemformularioid: 4,
                etiqueta: "Valor actual",
                ayuda:
                  "El próximo número que entregue será este más uno. Para que la numeración siga desde un punto, poné acá el último número usado.",
                requerido: true,
                respuesta: editando.valor
              }
            ]
          }
        ],
        botones: [{ etiqueta: "Guardar", callback: guardar, validar: true }]
      }
    : null;

  return (
    <>
      <Toast ref={toast} position="top-center" />

      <Sidebar
        visible={!!editando}
        onHide={() => setEditando(null)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>{editando?.nombre}</Titulo>
        {configFormulario && (
          <Formulario key={formKey} config={configFormulario} loader={loader} />
        )}
      </Sidebar>

      <Titulo urlVolver="/config">Contadores</Titulo>

      {!contadores && (
        <Card>
          <Skeleton width="100%" height="8rem" />
        </Card>
      )}

      {contadores && (
        <Card>
          <TablaFiltrable conFiltros={false}
            value={contadores}
            emptyMessage="Todavía no hay contadores"
            nombreArchivo="contadores"
          >
            <Column field="nombre" header="Contador" />
            <Column
              field="valor"
              header="Valor"
              style={{ width: "110px", textAlign: "center" }}
            />
            <Column
              header="Próximo"
              body={(c) => c.valor + 1}
              style={{ width: "110px", textAlign: "center" }}
            />
            <Column
              header="Origen"
              body={(c) => (
                <Tag
                  value={c.creadoAutomaticamente ? "Automático" : "Manual"}
                  severity={c.creadoAutomaticamente ? "info" : "secondary"}
                />
              )}
              style={{ width: "130px", textAlign: "center" }}
            />
            <Column
              header="Último uso"
              body={(c) => (c.ultimoUsoEn ? formatFecha(c.ultimoUsoEn) : "—")}
              style={{ width: "140px" }}
            />
            <Column
              header=""
              body={(c) => (
                <Boton
                  texto
                  icono
                  chico
                  negro
                  callback={() => {
                    setFormKey((k) => k + 1);
                    setEditando(c);
                  }}
                >
                  <i className="pi pi-pencil" />
                </Boton>
              )}
              style={{ width: "60px", textAlign: "center" }}
            />
          </TablaFiltrable>
        </Card>
      )}
    </>
  );
}
